import { useState } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { ThemeToggle } from '@usacow/ds';
import { NAV } from './nav';
import { useAuth } from './lib/auth';

/**
 * The student app shell: grouped sidebar nav + top bar. Responsive — the sidebar
 * collapses to an off-canvas drawer below the layout breakpoint (see app.css).
 * Screens render into <Outlet/>; they are built phase by phase.
 */
export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const title =
    NAV.flatMap((g) => g.items).find((n) => n.path === location.pathname)?.label ?? 'Home';
  const initials = (user?.display_name ?? user?.email ?? '?')
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className={`shell-side${navOpen ? ' is-open' : ''}`}>
        <div className="shell-brand">
          <img src="/logo-mark.svg" width={26} height={26} alt="" />
          <span className="shell-brand__name">usaCow</span>
        </div>
        <nav className="shell-nav" aria-label="Primary">
          {NAV.map((g) => (
            <div className="shell-nav__group" key={g.group}>
              <div className="shell-nav__gh">{g.group}</div>
              {g.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  end={item.path === '/app'}
                  className={({ isActive }) => `shell-nav__item${isActive ? ' is-active' : ''}`}
                  onClick={() => setNavOpen(false)}
                >
                  <span className="shell-nav__lbl">{item.label}</span>
                  {item.badge != null && <span className="shell-nav__badge">{item.badge}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {navOpen && <div className="shell-scrim" onClick={() => setNavOpen(false)} />}

      <div className="shell-main">
        <header className="shell-top">
          <button
            className="shell-burger"
            aria-label="Toggle navigation"
            onClick={() => setNavOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
          <h1 className="shell-top__title">{title}</h1>
          <div className="shell-top__r">
            <ThemeToggle />
            <Link to="/app/settings" className="shell-account" aria-label="Account and settings">
              {initials || 'AC'}
            </Link>
          </div>
        </header>
        <main className="shell-content" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
